'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@alphabait/ui' // card'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTsrClient } from '../../end-users/tsr'
import { ChevronDown, ChevronRight, Gift } from 'lucide-react'
import { ChartErrorState } from '@/components/charts/question-renderers/shared/chart-error-state'
import { ChartLoadingState } from '@/components/charts/question-renderers/shared/chart-loading-state'
import { ChartEmptyState } from '@/components/charts/question-renderers/shared/chart-empty-state'

interface TopIncentive {
  incentiveId: string
  incentiveName: string
  incentiveType: string
  selectionCount: number
  totalUsers: number
  selectionPercentage: number
  totalVouchers: number
  usedVouchers: number
  freeVouchers: number
}

interface ChosenGift {
  parentIncentiveItemId: string
  parentIncentiveName: string
  giftIncentiveItemId: string
  giftIncentiveName: string
  giftType: string
  timesChosen: number
  sharePercentage: number
}

interface TopIncentivesProps {
  periodStart: string
  periodEnd: string
  customerId?: string
  campaignIds?: string[]
  campaignGroupId?: string
}

export function TopIncentives({
  periodStart,
  periodEnd,
  customerId,
  campaignIds,
  campaignGroupId,
}: TopIncentivesProps) {
  const { t } = useTranslation()
  const tsr = useTsrClient()
  const [expandedIds, setExpandedIds] = useState<string[]>([])

  const startTimestamp = new Date(periodStart).getTime() / 1000
  const endTimestamp = new Date(periodEnd).getTime() / 1000

  const {
    data: topIncentivesData,
    isPending: isLoading,
    error,
  } = tsr.dashboard.getTopIncentives.useQuery({
    queryKey: [
      'topIncentives',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        customerId,
        campaignIds,
        campaignGroupId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      },
    },
  })

  const {
    data: chosenGiftsData,
    isPending: isChosenGiftsLoading,
    error: chosenGiftsError,
  } = tsr.dashboard.getChosenGifts.useQuery({
    queryKey: [
      'chosenGifts',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        customerId,
        campaignIds,
        campaignGroupId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      },
    },
  })

  const incentives: TopIncentive[] = topIncentivesData?.body || []
  const chosenGifts: ChosenGift[] = chosenGiftsData?.body || []

  const toggleExpanded = (incentiveId: string) =>
    setExpandedIds((ids) =>
      ids.includes(incentiveId)
        ? ids.filter((id) => id !== incentiveId)
        : [...ids, incentiveId],
    )

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-6 w-6 text-purple-500" />
            {t('dashboard.top_incentives.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartLoadingState message={t('dashboard.loading')} />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-6 w-6 text-purple-500" />
            {t('dashboard.top_incentives.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartErrorState message={t('dashboard.charts.failed')} />
        </CardContent>
      </Card>
    )
  }

  if (incentives.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-6 w-6 text-purple-500" />
            {t('dashboard.top_incentives.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-96 flex items-center justify-center">
          <ChartEmptyState
            message={t('dashboard.top_incentives.no_data_available')}
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="items-center space-y-2">
          <div className="flex items-center justify-between">
            <p>{t('dashboard.top_incentives.title')}</p>{' '}
          </div>
          <p className="text-sm text-muted-foreground font-thin">
            {t('dashboard.top_incentives.description')}
          </p>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-h-[550px]">
        <div className="grid gap-3 md:grid-cols-1">
          {incentives.map((incentive, index) => {
            // A gift group is a container: its own count is how many recipients
            // were assigned it, never what they chose. The choices live one
            // level down, so only these rows expand.
            const isGiftGroup = incentive.incentiveType === 'gift_group'
            const isExpanded = expandedIds.includes(incentive.incentiveId)
            const gifts = chosenGifts.filter(
              (gift) => gift.parentIncentiveItemId === incentive.incentiveId,
            )

            return (
              <div key={index}>
                <div className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-light-blue-100 rounded-full text-primary font-bold text-lg">
                      {index + 1}
                    </div>
                    <div className="grid items-center">
                      <h4
                        className="font-medium text-secondary-foreground truncate max-w-[200px]"
                        title={incentive.incentiveName}
                      >
                        {incentive.incentiveName}
                      </h4>
                      <p className="text-sm text-muted-foreground font-thin">
                        {t('dashboard.top_incentives.selections_count', {
                          count: incentive.selectionCount,
                        })}{' '}
                        |{' '}
                        {t('dashboard.top_incentives.of_total_users', {
                          count: incentive.totalUsers,
                        })}
                      </p>
                      {incentive.totalVouchers > 0 && (
                        <p className="text-sm text-gray-50 font-thin mt-1">
                          {t('dashboard.top_incentives.voucher_codes', {
                            used: incentive.usedVouchers,
                            free: incentive.freeVouchers,
                            total: incentive.totalVouchers,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right grid items-center text-lg font-bold text-primary">
                      {incentive.selectionPercentage.toFixed(1)}%
                    </div>
                    {isGiftGroup && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(incentive.incentiveId)}
                        aria-expanded={isExpanded}
                        aria-label={t(
                          isExpanded
                            ? 'dashboard.top_incentives.hide_chosen_gifts'
                            : 'dashboard.top_incentives.show_chosen_gifts',
                        )}
                        className="text-muted-foreground hover:text-primary"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {isGiftGroup && isExpanded && (
                  <div className="ml-15 pl-4 border-l border-light-blue-100">
                    <p className="text-sm text-muted-foreground font-thin mb-1">
                      {t('dashboard.top_incentives.chosen_gifts_title')}
                    </p>
                    {isChosenGiftsLoading ? (
                      <ChartLoadingState message={t('dashboard.loading')} />
                    ) : chosenGiftsError ? (
                      <ChartErrorState message={t('dashboard.charts.failed')} />
                    ) : gifts.length === 0 ? (
                      <ChartEmptyState
                        message={t('dashboard.top_incentives.no_chosen_gifts')}
                      />
                    ) : (
                      gifts.map((gift) => (
                        <div
                          key={gift.giftIncentiveItemId}
                          className="flex items-center justify-between py-2"
                        >
                          <div className="grid items-center">
                            <h5
                              className="font-medium text-secondary-foreground truncate max-w-[200px]"
                              title={gift.giftIncentiveName}
                            >
                              {gift.giftIncentiveName}
                            </h5>
                            <p className="text-sm text-muted-foreground font-thin">
                              {t('dashboard.top_incentives.times_chosen', {
                                count: gift.timesChosen,
                              })}{' '}
                              | {t(`incentives.types.${gift.giftType}`)}
                            </p>
                          </div>
                          <div className="text-right font-bold text-primary">
                            {gift.sharePercentage.toFixed(1)}%
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
